<template>
  <div class="user-profile">
    <toggle-email-subscriptions v-if="getterPopupName === 'ToggleEmailSubscription'"
                        :subscriptionOptions="subscriptionOptions"
                        @refreshSubscription="refreshSubscription" />
    <change-password v-else-if="getterPopupName === 'ChangePassword'" />
    <user-2FA v-else-if="getterPopupName === 'User2FA'" :tfaValue="tfaValue"
              @refreshTFA="refresh2FA" />
    <setup-2FA v-else-if="getterPopupName === 'Setup2FA'" />

    <h5>User Profile</h5>

    <div class="profile-field">
      <b>Email</b>:
      <p>{{user.email}}</p>
    </div>

    <div class="profile-field">
      <b>Role</b>:
      <p>{{user.role}}</p>
    </div>

    <div class="profile-field" style="margin-bottom: 10px">
      <b>2FA</b>:
      <p v-if="tfaMsg">
        Enabled
        <i class="fa fa-info-circle">
          <md-tooltip md-direction="top">{{tfaMsg}}</md-tooltip>
        </i>
      </p>
      <md-switch v-model="user.tfa_enabled" @change="toggle2FA" v-else />

      <button class="icon-btn" @click="reset2FA" v-if="user.tfa_enabled">
        <i class="fa fa-redo" />
        Reset 2FA
      </button>
      <button class="icon-btn" @click="openPopup('Setup2FA')">
        <i class="fa fa-info-circle"></i>
        How to setup 2FA?
      </button>
    </div>

    <md-field class="no-right-border" :class="{'input-error': $v.user.name.$error || formErrors.name.length}">
      <label>Name</label>
      <div class="custom-field">
        <md-input v-model="user.name" @blur="$v.user.name.$touch()"
                  @keyup.enter="updateName" :disabled="disableData.name" />

        <span class="input-suffix" v-if="disableData.name" @click="disableData.name = false">
          <i class="fa fa-pencil-alt" />
        </span>
        <span class="input-suffix" v-else>
          <i class="fa fa-times-circle" @click="cancelEdit()" />
          <i class="fa fa-check-circle" @click="updateName" />
        </span>
      </div>
    </md-field>
    <h6 class="error-message" v-if="$v.user.name.$error && !$v.user.name.required">Please enter a user name</h6>
    <h6 class="error-message" v-else-if="$v.user.name.$error && !$v.user.name.alphaSpace">Please enter a valid user name with alphabets &amp; space only</h6>
    <error-info v-else :errorList="formErrors.name" />

    <button class="icon-btn password" @click="openPopup('ChangePassword')">
      <i class="fa fa-lock" />
      <span>Change Account Password</span>
    </button>

    <hr>

    <h5>Email Subscriptions</h5>

    <div v-for="(data, index) in emailSubscriptions" :key="index" class="email-subscriptions">
      <p>{{data.text}}</p>
      <md-switch v-model="data.enabled" @change="toggleSubscription(data)" />
    </div>

    <table-loader v-if="!dataAdded" />

    <hr>

    <h5>Active Sessions <span v-if="dataAdded">({{userSessions.length}})</span></h5>

    <user-sessions :userSessions="userSessions" :sessionsAdded="dataAdded"
                  @refreshUserSessions="(newVal) => userSessions = newVal" />
  </div>
</template>

<script>
import ToggleEmailSubscriptions from '@/components/Settings/UserProfile/ToggleEmailSubscriptions'
import ChangePassword from '@/components/Settings/UserProfile/ChangePassword'
import UserSessions from '@/components/Settings/UserProfile/UserSessions'
import ErrorInfo from '@/components/global/ErrorInfo'
import TableLoader from '@/components/global/TableLoader'
import User2FA from '@/components/TFA/User2FA'
import Setup2FA from '@/components/TFA/Setup2FA'

import ApiServices from '@/services/api/userProfile'
import { required } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'
import { openPopup, checkFormErrors } from '@/utils/helpers'
import { alphaSpace } from '@/utils/helpers/validators'

export default {
  name: 'SettingsUserProfile',
  components: { ToggleEmailSubscriptions, UserSessions, ChangePassword, ErrorInfo, User2FA, Setup2FA, TableLoader },
  data () {
    return {
      user: {},
      emailSubscriptions: [],
      userSessions: [],
      disableData: {
        name: true
      },
      tfaValue: false,
      tfaMsg: null,
      backupData: {},
      formErrors: {
        name: []
      },
      dataAdded: false
    }
  },
  validations: {
    user: {
      name: {
        required,
        alphaSpace
      }
    }
  },
  watch: {
    'user.name' () {
      this.formErrors.name = []
    }
  },
  computed: {
    ...mapGetters(['getterPopupName'])
  },
  created () {
    this.populateData()
  },
  methods: {
    openPopup,
    checkFormErrors,
    populateData () {
      ApiServices.profileDetails().then(response => {
        let data = response.data
        this.user = data.user
        this.tfaValue = this.user.tfa_enabled
        this.emailSubscriptions = data.email_alerts
        this.userSessions = data.sessions
        this.dataAdded = true

        if (data.partner.tfa_required) {
          this.tfaMsg = data.partner.tfa_required_message
        }

        this.backupData = {
          name: this.user.name
        }
      })
    },
    cancelEdit () {
      this.disableData.name = true
      this.user.name = this.backupData.name
      this.$v.$reset()
    },
    updateName () {
      this.$v.$touch()
      if (!this.$v.$error && !this.checkFormErrors(this.formErrors)) {
        ApiServices.updateDetails({name: this.user.name}).then(response => {
          this.disableData.name = true
          this.successToastMsg(response.message)

          this.user = response.data
          this.backupData = {
            name: this.user.name
          }
        }).catch(error => {
          this.formErrors = {
            ...this.formErrors,
            ...error.body.reason
          }
        })
      }
    },
    subscribe (data, type) {
      data['title'] = `${type} Email Subscription`
      data['type'] = type
      this.subscriptionOptions = data
      this.openPopup('ToggleEmailSubscription')
    },
    toggleSubscription (data) {
      data.enabled = !data.enabled
      if (data.enabled) {
        this.subscribe(data, 'Disable')
      } else {
        this.subscribe(data, 'Enable')
      }
    },
    refreshSubscription () {
      let currentSubscription = this.emailSubscriptions.find(element => element.category === this.subscriptionOptions.category)
      currentSubscription.enabled = !currentSubscription.enabled
    },
    toggle2FA () {
      this.user.tfa_enabled = !this.user.tfa_enabled
      this.tfaValue = !this.user.tfa_enabled
      this.openPopup('User2FA')
    },
    reset2FA () {
      this.tfaValue = 'Reset'
      this.openPopup('User2FA')
    },
    refresh2FA (updatedVal) {
      this.user.tfa_enabled = updatedVal
      this.openPopup(null)
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/customField.scss';

  .user-profile {
    text-align: left;

    h5 {
      @include highlight-text($secondary, 18px);
      margin-bottom: 10px;
    }

    .profile-field {
      @include custom-flex(null);
      margin: 15px 0;

      b {
        color: $grey-text;
        min-width: 80px;
        font-size: 14px;
      }
      p {
        font-weight: 500;
        font-size: 14px;
        padding-left: 10px;

        i {
          padding-right: 10px;
          border-right: 2px solid $grey-outline;
          cursor: pointer;

          &.fa-info-circle {
            color: $primary;
          }
        }
      }

      .md-switch {
        padding-left: 10px;
        margin: 0 !important;
        margin-left: 0 !important;
      }

      .icon-btn {
        margin-left: 10px;
      }
    }

    hr {
      border-bottom: $divider;
      border-top: none;
      margin: 20px 0;
    }

    .password {
      margin-top: 10px;
    }

    .email-subscriptions {
      @include custom-flex(space-between);
      margin: 10px 0;

      p {
        @include highlight-text($secondary, 13px, 500);
      }
    }
  }

  .desktop {
    .user-profile {
      .md-field {
        width: 70%;
      }

      .email-subscriptions {
        width: 70%;

        .md-switch {
          margin-left: 10% !important;
        }
      }
    }
  }

  .tablet {
    .user-profile {
      .md-field, .email-subscriptions {
        max-width: 400px;
      }
    }
  }
</style>
